import { LoadingState } from "@repo/app-types/client/client";
import type { Project } from "@repo/app-types/entities/project";
import { buildProjectUrlPath } from "@repo/app-types/helpers/adminAppRoutes";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Input } from "@repo/ui/Inputs/input";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { SimpleRadioGroup } from "@repo/ui/Radio/simpleRadioGroup";
import { SimpleRadioOption } from "@repo/ui/Radio/simpleRadioOption";
import { type FC, useState } from "react";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../../notificationsOverlay/notificationsSlice.js";
import { selectPhoneNumberAndExtensionForProject } from "../../../phoneNumbers/phoneNumbersSlice.js";
import { createRecruitingProject } from "../../projectsSlice.js";
import { JobOpeningDetailsInputs } from "./jobOpeningSettings.js";

interface DuplicateJobOpeningModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
}

const DuplicationMode = {
  ShareJobType: "share_job_type",
  DuplicateJobType: "duplicate_job_type",
} as const;

type DuplicationMode = (typeof DuplicationMode)[keyof typeof DuplicationMode];

export const DuplicateJobOpeningModal: FC<DuplicateJobOpeningModalProps> = ({
  isOpen,
  onClose,
  project,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const phoneNumberInfo = useAppSelector(
    selectPhoneNumberAndExtensionForProject(project.id),
  );

  const [customJobOpeningName, setCustomJobOpeningName] = useState<
    string | undefined
  >();
  const [jobTypeName, setJobTypeName] = useState(project.job_type?.name ?? "");
  const [location, setLocation] = useState("");
  const [pay, setPay] = useState(project.pay?.description ?? "");
  const [hours, setHours] = useState(project.hours?.description ?? "");
  const [duplicationMode, setDuplicationMode] = useState<DuplicationMode>(
    DuplicationMode.ShareJobType,
  );
  const [createProjectStatus, setCreateProjectStatus] = useState<LoadingState>(
    LoadingState.LOADED,
  );

  const shouldDuplicateJobType =
    duplicationMode === DuplicationMode.DuplicateJobType;

  const computeDefaultJobOpeningName = () => {
    const baseName = shouldDuplicateJobType
      ? jobTypeName
      : (project.job_type?.name ?? "");

    if (!baseName) {
      return "";
    }

    if (!location) {
      return baseName;
    }

    return `${baseName} - ${location}`;
  };

  const jobOpeningName = customJobOpeningName ?? computeDefaultJobOpeningName();

  const onClickDuplicate = async () => {
    if (!project.job_type) return;
    setCreateProjectStatus(LoadingState.LOADING);

    try {
      const newProject = await dispatch(
        createRecruitingProject({
          name: jobOpeningName,
          phoneNumberId: phoneNumberInfo?.phoneNumber.id,
          jobTypeId: project.job_type.id,
          jobTypeName: shouldDuplicateJobType ? jobTypeName : undefined,
          location: location ? { description: location } : undefined,
          pay: pay ? { description: pay } : undefined,
          hours: hours ? { description: hours } : undefined,
          shouldDuplicateJobType,
        }),
      ).unwrap();

      const newProjectId = newProject.id;

      showNotification(dispatch, {
        id: `project-created-${new Date().getTime().toString()}`,
        primaryMessage: `Job opening duplicated`,
        type: NotificationType.SUCCESS,
      });

      setCreateProjectStatus(LoadingState.LOADED);
      navigate(buildProjectUrlPath(newProjectId));
      onClose();
    } catch (err) {
      console.error("error creating project", err);
      setCreateProjectStatus(LoadingState.ERROR);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onCancel={onClose}>
      <ModalHeader>Duplicate job opening</ModalHeader>
      <div className="mt-4 flex flex-col space-y-3">
        <SimpleRadioGroup
          value={duplicationMode}
          onChange={(value) => setDuplicationMode(value as DuplicationMode)}
        >
          <SimpleRadioOption value={DuplicationMode.ShareJobType}>
            Reuse the same job type
          </SimpleRadioOption>
          <SimpleRadioOption value={DuplicationMode.DuplicateJobType}>
            Duplicate the job type
          </SimpleRadioOption>
        </SimpleRadioGroup>
        {!shouldDuplicateJobType ? (
          <InfoBanner
            title="Questions and criteria will be shared"
            description={
              <>
                The new job opening will use the{" "}
                <strong>{project.job_type?.name}</strong> job type and will
                share the same questions and assessment criteria.
              </>
            }
          />
        ) : (
          <InfoBanner
            title="Questions and criteria will be duplicated"
            description={
              <>
                The new job opening will use a copy of the{" "}
                <strong>{project.job_type?.name}</strong> job type so that
                questions and assessment criteria can be changed without
                affecting other job openings.
              </>
            }
          />
        )}
        {shouldDuplicateJobType ? (
          <Input
            label="Job type name"
            onChange={(evt) => {
              const newJobTypeName = evt.target.value;
              setJobTypeName(newJobTypeName);
            }}
            placeholder={project.job_type?.name ?? "Job type name"}
            required
            value={jobTypeName}
            maxLength={100}
          />
        ) : null}
        <JobOpeningDetailsInputs
          location={location}
          pay={pay}
          hours={hours}
          onLocationChange={setLocation}
          onPayChange={setPay}
          onHoursChange={setHours}
        />
        <Input
          label="Job opening name"
          onChange={(evt) => {
            setCustomJobOpeningName(evt.target.value);
          }}
          placeholder="Store Manager - San Francisco"
          required
          value={jobOpeningName}
          maxLength={100}
        />
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <div className="ml-3">
          <Button
            isDisabled={
              !jobOpeningName ||
              (shouldDuplicateJobType && !jobTypeName) ||
              createProjectStatus === LoadingState.LOADING
            }
            isLoading={createProjectStatus === LoadingState.LOADING}
            onClick={() => void onClickDuplicate()}
            variant={ButtonVariant.Primary}
          >
            Create new job opening
          </Button>
        </div>
        <Button onClick={onClose} variant={ButtonVariant.Secondary}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};
